import React, { useState } from 'react';
import styled from 'styled-components';
import WelcomeScreen from './components/WelcomeScreen';
import CompanyDataScreen from './components/CompanyDataScreen';
import LocationPaymentScreen from './components/LocationPaymentScreen';
import TaxasScreen from './components/TaxasScreen';
import PaymentScreen from './components/PaymentScreen';
import StartupQuestionsScreen from './components/StartupQuestionsScreen';
import StartupPlansScreen from './components/StartupPlansScreen';
import EstablishedCompanyPlanScreen from './components/EstablishedCompanyPlanScreen';
import CompletionScreen from './components/CompletionScreen';
import { ThemeProvider } from 'styled-components';
import { sgfService } from './services/api';

const theme = {
  colors: {
    primary: '#FF7F00',
    secondary: '#FFB347',
    text: '#F8FAFC',
    lightText: 'rgba(248, 250, 252, 0.75)',
    background: '#040D2D',
    error: '#F87171',
    success: '#34D399',
    border: 'rgba(148, 163, 184, 0.45)',
    lightBackground: 'rgba(15, 23, 42, 0.65)'
  },
  fonts: {
    main: 'Roboto, sans-serif'
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px'
  }
};

const AppContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #020617 0%, #0f172a 40%, #0b1229 100%);
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.text};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const SidebarContainer = styled.div`
  width: 320px;
  background: rgba(4, 13, 45, 0.92);
  backdrop-filter: blur(14px);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(148, 163, 184, 0.18);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  }
`;

const MainContainer = styled.div`
  flex: 1;
  padding: 3rem 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.tight ? '1.25rem' : '1.75rem'};
  background: #FF7F00;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    padding: 3rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    padding: 2.5rem 1.5rem 2rem;
  }
`;

const FormContainer = styled.div`
  width: ${props => props.wide ? '90%' : '100%'};
  max-width: ${props => props.wide ? '960px' : '720px'};
  background: rgba(15, 23, 42, 0.88);
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 28px 50px rgba(15, 23, 42, 0.45);
  padding: ${props => props.wide ? '2.5rem' : '2.75rem'};
  margin-top: ${props => props.wide ? '0.25rem' : '0'};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.wide ? '2rem' : '2rem'};
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 1.5rem;
`;

const Logo = styled.img`
  max-width: 120px;
  height: auto;
`;

const SidebarHeading = styled.h2`
  font-size: 1.6rem;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.text};
  font-weight: 600;
`;

const SidebarDescription = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.lightText};
  line-height: 1.5;
  margin: 0;
`;

const StepperContainer = styled.div`
  margin-top: 2.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StepList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StepItem = styled.li`
  display: grid;
  grid-template-columns: 56px 1fr;
  column-gap: 1rem;
  position: relative;
  align-items: flex-start;
`;

const IndicatorColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

const StepIndicator = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid ${props => (props.isActive || props.isCompleted) ? props.theme.colors.primary : 'rgba(148, 163, 184, 0.35)'};
  background: ${props => (props.isActive || props.isCompleted) ? 'rgba(255, 127, 0, 0.15)' : 'rgba(15, 23, 42, 0.75)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => (props.isActive || props.isCompleted) ? props.theme.colors.primary : 'rgba(148, 163, 184, 0.6)'};
  transition: all 0.3s ease;
  position: relative;
  box-shadow: ${props => props.isActive ? '0 12px 30px rgba(255, 127, 0, 0.25)' : 'none'};
`;

const StepConnector = styled.div`
  flex: 1;
  width: 2px;
  background: ${props => props.isCompleted ? props.theme.colors.primary : 'rgba(148, 163, 184, 0.25)'};
  margin-top: 0.75rem;
  border-radius: 999px;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const StepLabel = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${props => (props.isActive || props.isCompleted) ? props.theme.colors.text : props.theme.colors.lightText};
  transition: color 0.3s ease;
`;

const StepCaption = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.lightText};
  opacity: ${props => (props.isActive ? 1 : 0.7)};
  line-height: 1.4;
  transition: opacity 0.3s ease;
`;

const StepInfoHeader = styled.div`
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StepPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  background: rgba(255, 127, 0, 0.15);
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
`;

const StepHeading = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.colors.text};
`;

const StepLead = styled.p`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
`;

const iconProps = {
  width: 22,
  height: 22,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  fill: 'none'
};

const MailIcon = (props) => (
  <svg viewBox="0 0 24 24" {...iconProps} {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
    <polyline points="3 7 12 13 21 7" />
  </svg>
);

const BuildingIcon = (props) => (
  <svg viewBox="0 0 24 24" {...iconProps} {...props}>
    <rect x="3" y="9" width="6" height="12" rx="1" />
    <rect x="11" y="5" width="10" height="16" rx="1" />
    <path d="M7 13h0" />
    <path d="M7 17h0" />
    <path d="M15 9h0" />
    <path d="M15 13h0" />
    <path d="M15 17h0" />
  </svg>
);

const MapPinIcon = (props) => (
  <svg viewBox="0 0 24 24" {...iconProps} {...props}>
    <path d="M21 10c0 5.25-9 11-9 11s-9-5.75-9-11a9 9 0 1 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const DocumentIcon = (props) => (
  <svg viewBox="0 0 24 24" {...iconProps} {...props}>
    <path d="M14 3H6a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="14 3 14 9 20 9" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="15" y2="17" />
  </svg>
);

const FlagIcon = (props) => (
  <svg viewBox="0 0 24 24" {...iconProps} {...props}>
    <path d="M4 4h11l-1 4 4 1-1 4H4" />
    <line x1="4" y1="20" x2="4" y2="4" />
  </svg>
);

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [formData, setFormData] = useState({
    nome_responsavel: '',
    email_responsavel: '',
    nome_fantasia: '',
    razao_social: '',
    website: '',
    is_startup: false,
    celular_zap_responsavel: '',
    cargo_responsavel: '',
    cnpj: '',
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    breve_descricao: '',
    filiada_produtos_servicos: '',
    instagram: '',
    observacoes: '',
    dia_vencimento: '',
    is_startup_question1: null,
    is_startup_question2: null,
    is_startup_question3: null,
    qualifies_as_startup: false,
    selected_plan: null,
    asaas_customer_id: ''
  });

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const formSteps = [
    {
      id: 1,
      title: 'Seus dados de contato',
      description: 'Informe o e-mail, cargo e telefone do responsável pelo cadastro.',
      sidebar: 'E-mail, cargo e telefone do responsável.',
      Icon: MailIcon,
      label: 'Contato'
    },
    {
      id: 2,
      title: 'Dados da empresa',
      description: 'Conte o nome fantasia, site e CNPJ da sua empresa.',
      sidebar: 'Nome fantasia, site e CNPJ.',
      Icon: BuildingIcon,
      label: 'Empresa'
    },
    {
      id: 3,
      title: 'Endereço',
      description: 'Localize a empresa com CEP, rua, número e estado.',
      sidebar: 'Cep, rua, número e estado.',
      Icon: MapPinIcon,
      label: 'Endereço'
    },
    {
      id: 4,
      title: 'Descrição da atuação',
      description: 'Descreva brevemente a empresa e os produtos ou serviços oferecidos.',
      sidebar: 'Resumo da atuação e serviços.',
      Icon: DocumentIcon,
      label: 'Descrição'
    },
    {
      id: 5,
      title: 'Confirmação',
      description: 'Confira todos os dados antes de escolher o plano.',
      sidebar: 'Confira e finalize.',
      Icon: FlagIcon,
      label: 'Confirmação'
    },
    {
      id: 6,
      title: 'Elegibilidade',
      description: 'Responda as perguntas para identificar o plano ideal.',
      sidebar: 'Perguntas sobre a empresa.',
      Icon: FlagIcon,
      label: 'Elegibilidade'
    }
  ];

  const totalSteps = formSteps.length;
  const effectiveStep = currentStep > totalSteps ? totalSteps : currentStep;
  const activeStep = formSteps[effectiveStep - 1];
  const isFinalReview = currentStep === 6;

  const submitForm = async (dataToSubmit = formData) => {
    try {
      console.log('Dados do formulário enviados:', dataToSubmit);
      
      // Enviar dados para a API SGF
      const responseData = await sgfService.sendFiliada(dataToSubmit);
      console.log('Resposta da API SGF:', responseData);
      
      setHasError(false);
      nextStep();
      
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setHasError(true);
      nextStep();
    }
  };
  
  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return <WelcomeScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} />;
      case 2:
        return <CompanyDataScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} />;
      case 3:
        return <LocationPaymentScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} />;
      case 4:
        return <TaxasScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} />;
      case 5:
        return <PaymentScreen formData={formData} prevStep={prevStep} submitForm={submitForm} />;
      case 6:
        return <StartupQuestionsScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} />;
      case 7:
        // Se qualifica como startup, mostra planos startup, senão mostra plano estabelecido
        return formData.qualifies_as_startup 
          ? <StartupPlansScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} />
          : <EstablishedCompanyPlanScreen formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} />;
      case 8:
        return <CompletionScreen formData={formData} submitForm={submitForm} hasError={hasError} />;
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AppContainer>
        <SidebarContainer>
          <LogoContainer>
            <Logo src="/images/setirn.png" alt="SETIRN Logo" />
            <Logo src="/images/eptec-logo.png" alt="EPTEC Logo" />
          </LogoContainer>
          <SidebarHeading>Formulário de Filiação</SidebarHeading>
          <SidebarDescription>
            Siga as etapas para concluir o cadastro da sua empresa no SETIRN.
          </SidebarDescription>
          <StepperContainer>
            <StepList>
              {formSteps.map((step, index) => {
                const StepIcon = step.Icon;
                const position = index + 1;
                const isActive = currentStep <= totalSteps && effectiveStep === position;
                const isCompleted = currentStep > position;

                return (
                  <StepItem key={step.id}>
                    <IndicatorColumn>
                      <StepIndicator isActive={isActive} isCompleted={isCompleted}>
                        <StepIcon />
                      </StepIndicator>
                      {index !== formSteps.length - 1 && (
                        <StepConnector isCompleted={currentStep > position} />
                      )}
                    </IndicatorColumn>
                    <StepContent>
                      <StepLabel isActive={isActive} isCompleted={isCompleted}>
                        {step.label}
                      </StepLabel>
                      <StepCaption isActive={isActive}>{step.sidebar}</StepCaption>
                    </StepContent>
                  </StepItem>
                );
              })}
            </StepList>
          </StepperContainer>
        </SidebarContainer>
        <MainContainer tight={isFinalReview}>
          {currentStep <= totalSteps && activeStep && (
            <StepInfoHeader>
              <StepPill>Passo {effectiveStep} de {totalSteps}</StepPill>
              <StepHeading>{activeStep.title}</StepHeading>
              {activeStep.description && (
                <StepLead>{activeStep.description}</StepLead>
              )}
            </StepInfoHeader>
          )}
          <FormContainer wide={isFinalReview}>
            {renderStep()}
          </FormContainer>
        </MainContainer>
      </AppContainer>
    </ThemeProvider>
  );
}

export default App;
